import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import langDocumentAccessTable from '../../../document/langs/langDocumentAccessTable';
import { GridRenderCellParams, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { Box, Button, Grid, TextField } from '@mui/material';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import useDocumentTypes from '../../../document/hooks/useDocumentTypes';
import langDocumentType from '../../../document/langs/langDocumentType';
import AppFormatDateTime from '../../../../app/features/format/AppFormatDateTime';
import moment from 'moment';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppFormKeyboardDatePicker from '../../../../app/components/Form/CAppFormKeyboardDatePicker';
import CAppFilterSelect from '../../../../app/components/CAppFilterSelect';
import IFund from '../../../investor/interfaces/fund/IFund';
import _ from 'lodash';
import IDocumentType from '../../../document/interfaces/IDocumentType';
import HDocumentFile from '../../../document/helpers/HDocumentFile';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import CAppFormTextField from '../../../../app/components/Form/CAppFormTextField';
import useDocumentAccess from '../../../document/hooks/useDocumentAccess';
import * as XLSX from 'xlsx';
import langAdminDocumentAccess from '../../langs/langAdminDocumentAccess';
import IInvestorGroup from '../../../investor/interfaces/IInvestorGroup';
import { TInvestExpertiseTag } from '@/apps/investor/types/expertise/TInvestExpertiseTag';
import investmentTypeLang from '@/apps/investor/features/fund-type/langs/investmentTypeLang';
import AppLoaderContent from '@/app/features/loader/content/AppLoaderContent';
import { langLookup } from '../../../../app/utils/langLookup';

type DocumentAccessFilter = {
	keywords: string;
	startDate: Date;
	endDate: Date;
	startReleaseDate?: Date;
	endReleaseDate?: Date;
	fundIds: number[];
	groupIds: number[];
	documentTypeTag?: string;
	fundTypeTag?: string;
};

type IFundTypeTag = { tag: TInvestExpertiseTag; name: string };

const FDocumentAccessTable = () => {
	const [filters, setFilters] = useState<DocumentAccessFilter>({
		keywords: '',
		startDate: moment().subtract(1, 'month').toDate(),
		endDate: moment().add(1, 'day').toDate(),
		fundIds: [],
		groupIds: [],
	});

	const { data: logs, isLoading } = useDocumentAccess(filters?.startDate, filters?.endDate);
	const { documentTypes } = useDocumentTypes();

	const { t } = useTranslation();

	const [rows, setRows] = useState<GridRowsProp>();

	const [funds, setFunds] = useState<IFund[]>();
	const [groups, setGroups] = useState<IInvestorGroup[]>();
	const [docTypes, setDocTypes] = useState<IDocumentType[]>();
	const [fundTypeTag, setFundTypeTag] = useState<IFundTypeTag[]>();

	useEffect(() => {
		if (logs) {
			setFunds(
				_.uniqBy(
					logs.map((x) => x.fund),
					'id',
				),
			);
			setGroups(
				_.uniqBy(
					logs.filter((x) => x.company).map((x) => x.company),
					'id',
				),
			);
		}
	}, [logs]);

	useEffect(() => {
		if (logs && documentTypes) {
			setDocTypes(
				_.uniqBy(
					logs
						.map((x) => {
							const documentType = _.cloneDeep(documentTypes).find(
								(t) => t.tag === x.document.documentType,
							);
							if (documentType) {
								documentType.name =
									t(langLookup(langDocumentType, documentType.name)?.tag ?? '') || documentType.name;
							}
							return documentType;
						})
						.filter((x) => x) as IDocumentType[],
					'tag',
				),
			);
			setFundTypeTag(
				_.uniqBy(
					logs
						.map((x) => {
							const typeTag = x.fund.typeTag;
							if (typeTag) {
								return {
									tag: typeTag,
									name: t(investmentTypeLang[typeTag].tag) || typeTag,
								};
							}
						})
						.filter((x) => x) as IFundTypeTag[],
					'tag',
				),
			);
		}
	}, [documentTypes, logs, t]);

	useEffect(() => {
		if (logs && documentTypes && filters) {
			let logsTmp = _.cloneDeep(logs);
			if (filters.fundIds.length > 0) {
				logsTmp = logsTmp.filter((x) => filters.fundIds.includes(x.fund.id));
			}
			if (filters.groupIds.length > 0) {
				logsTmp = logsTmp.filter((x) => filters.groupIds.includes(x.company?.id));
			}
			if (filters.documentTypeTag) {
				logsTmp = logsTmp.filter((x) => filters.documentTypeTag === x.document.documentType);
			}
			if (filters.fundTypeTag) {
				logsTmp = logsTmp.filter((x) => filters.fundTypeTag === x.fund?.typeTag);
			}

			logsTmp = logsTmp.filter((x) => x.isImpersonated !== true);

			if (filters.keywords) {
				const searchWords = filters.keywords.split(' ');
				searchWords.forEach((word) => {
					logsTmp = logsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.document.name).includes(HDocumentFile.cleanWord(word)),
					);
				});
			}
			if (filters.startReleaseDate) {
				logsTmp = logsTmp.filter(
					(x) =>
						moment(moment(x.document.releaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.startReleaseDate).format('YYYY-MM-DD')),
						) >= 0,
				);
			}
			if (filters.endReleaseDate) {
				logsTmp = logsTmp.filter(
					(x) =>
						moment(moment(x.document.releaseDate).format('YYYY-MM-DD')).diff(
							moment(moment(filters.endReleaseDate).format('YYYY-MM-DD')),
						) <= 0,
				);
			}

			setRows(
				logsTmp.map((x, position) => {
					const documentType = documentTypes.find((t) => t.tag === x.document.documentType);
					return {
						id: position,
						name: x.document.name,
						documentTypeName:
							documentType && langLookup(langDocumentType, documentType.name)
								? t(langLookup(langDocumentType, documentType.name)?.tag ?? '')
								: x.document.documentType,
						groupName: x.company?.name,
						['fund.name']: x.fund?.name,
						accessTime: x.accessTime,
						releaseDate: x.document?.releaseDate,
						isImpersonated: x.isImpersonated,
						expertise: x.fund.typeTag ? t(investmentTypeLang[x.fund.typeTag].tag) : '',
					};
				}),
			);
		}
	}, [documentTypes, logs, t, filters]);

	const handleExport = () => {
		if (!rows) return;

		const headers = [
			t(langDocumentAccessTable.document_name.tag),
			t(langDocumentAccessTable.document_type.tag),
			t(langDocumentAccessTable.fund.tag),
			t(langDocumentAccessTable.group.tag),
			t(langDocumentAccessTable.release_date.tag),
			t(langDocumentAccessTable.access_time.tag),
			t(langDocumentAccessTable.expertise.tag),
		];
		const data = rows.map((row) => ({
			name: row.name,
			documentTypeName: row.documentTypeName,
			fundName: row['fund.name'],
			groupName: row.groupName,
			releaseDate: moment(row.releaseDate).format('DD/MM/YYYY'),
			accessTime: moment(row.accessTime).format('DD/MM/YYYY'),
			expertise: row.expertise,
		}));

		const worksheet = XLSX.utils.json_to_sheet(data);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');

		worksheet['!cols'] = _.keys(data[0]).map((field, index) => {
			const header = headers[index] ?? '';
			return {
				wch: Math.max(
					header.length,
					Math.max(...data.map((obj: Record<string, unknown>) => String(obj[field] ?? '').length)),
				),
			};
		});

		XLSX.writeFileXLSX(workbook, 'Document access logs.xlsx');
	};

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t(langDocumentAccessTable.document_name.tag),
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'documentTypeName',
			headerName: t(langDocumentAccessTable.document_type.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'fund.name',
			headerName: t(langDocumentAccessTable.fund.tag),
			width: 250,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'groupName',
			headerName: t(langDocumentAccessTable.group.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'expertise',
			headerName: t(langDocumentAccessTable.expertise.tag),
			width: 100,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'releaseDate',
			headerName: t(langDocumentAccessTable.release_date.tag),
			width: 150,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'accessTime',
			headerName: t(langDocumentAccessTable.access_time.tag),
			width: 200,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDateTime date={params.value} />,
		},
	];

	return (
		<>
			{rows ? (
				<>
					<Box sx={{ marginBottom: '1rem', marginTop: '2rem' }}>
						<Grid container spacing={3}>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentAccess.filter_start_download_date.tag)}
									value={filters?.startDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, startDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentAccess.filter_end_download_date.tag)}
									value={filters?.endDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, endDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={5}>
								<TextField
									variant="outlined"
									fullWidth
									size="small"
									label={t(langAdminDocumentAccess.filter_keywords.tag)}
									value={filters?.keywords}
									onChange={(event) =>
										setFilters((prevData) => ({ ...prevData, keywords: event.target.value }))
									}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '45px',
										},
										'& .MuiInputBase-input': {
											paddingLeft: '10px',
										},
										marginBottom: '15px',
									}}
									InputProps={{
										startAdornment: <AppCustomIcon tag="search" />,
									}}
								/>
							</Grid>

							<Grid item sm={3}>
								<CAppFilterSelect
									multiple
									multipleRenderTags={(val: IFund[]) => (
										<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
											{val ? val.length : 0} fund(s) selected
										</Box>
									)}
									value={
										funds
											? funds.filter(
													(x) => filters && filters.fundIds && filters.fundIds.includes(x.id),
											  )
											: null
									}
									onChange={(val: IFund[]) => {
										setFilters((prevData) => ({
											...prevData,
											fundIds: val && val.length > 0 ? val.map((x) => x.id) : [],
										}));
									}}
									label={t(langAdminDocumentAccess.filter_funds.tag)}
									size="small"
									options={
										funds
											? funds.map((x: IFund) => ({
													id: x.id,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>

							<Grid item sm={4}>
								<CAppFilterSelect
									multiple
									multipleRenderTags={(val: IInvestorGroup[]) => (
										<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
											{val ? val.length : 0} group(s) selected
										</Box>
									)}
									value={
										groups
											? groups.filter(
													(x) =>
														filters && filters.groupIds && filters.groupIds.includes(x.id),
											  )
											: null
									}
									onChange={(val: IInvestorGroup[]) => {
										setFilters((prevData) => ({
											...prevData,
											groupIds: val && val.length > 0 ? val.map((x) => x.id) : [],
										}));
									}}
									label={t(langAdminDocumentAccess.filter_groups.tag)}
									size="small"
									options={
										groups
											? groups.map((x: IInvestorGroup) => ({
													id: x.id,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFilterSelect
									value={docTypes ? docTypes.find((x) => x.tag === filters.documentTypeTag) : null}
									onChange={(val: IDocumentType) => {
										setFilters((prevData) => ({
											...prevData,
											documentTypeTag: val ? val.tag : undefined,
										}));
									}}
									label={t(langAdminDocumentAccess.filter_type.tag)}
									size="small"
									options={
										docTypes
											? docTypes.map((x: IDocumentType) => ({
													tag: x.tag,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFilterSelect
									value={fundTypeTag?.find((x) => x.tag === filters.fundTypeTag)}
									onChange={(val: IDocumentType) => {
										setFilters((prevData) => ({
											...prevData,
											fundTypeTag: val?.tag,
										}));
									}}
									label={t(langAdminDocumentAccess.filter_expertise.tag)}
									size="small"
									options={fundTypeTag?.map((x: IFundTypeTag) => ({
										tag: x.tag,
										name: x.name,
									}))}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentAccess.filter_start_released_date.tag)}
									value={filters?.startReleaseDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, startReleaseDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentAccess.filter_end_released_date.tag)}
									value={filters?.endReleaseDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, endReleaseDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={2}>
								<Button color="secondary" variant="contained" fullWidth onClick={() => handleExport()}>
									{t(langAdminDocumentAccess.btn_export_excel.tag)}
								</Button>
							</Grid>
						</Grid>
					</Box>
					{isLoading ? (
						<AppLoaderContent sx={{ height: '200px', minHeight: '200px' }} />
					) : (
						<CAppDataGrid
							className="document-files-desktop"
							getRowClassName={(params) => `row-status--${params.row.fund?.isRealized}`}
							rows={rows}
							columns={columns}
							autoHeight
							pagination
							disableSelectionOnClick
							disableColumnSelector
							disableColumnMenu
							initialState={{
								sorting: {
									sortModel: [
										{
											field: 'accessTime',
											sort: 'desc',
										},
									],
								},
							}}
						/>
					)}
				</>
			) : (
				''
			)}
		</>
	);
};

export default FDocumentAccessTable;
